<template>
  <main class="index-page">
    <header class="page-header">
      <div>
        <h1>渠道管理</h1>
        <p>配置上游 API 渠道，按模型名称选择可用渠道进行转发。</p>
      </div>
      <el-button type="primary" @click="openCreateDialog">新增渠道</el-button>
    </header>

    <el-alert v-if="notice" class="notice-alert page-notice" :title="notice" type="success" show-icon :closable="false" />

    <ChannelTable
      :channels="channels"
      :loading="loading"
      @edit="openEditDialog"
      @delete="removeChannel"
      @test="runChannelTest"
    />

    <el-dialog
      v-model="formDialog.visible"
      :title="editingChannel ? '编辑渠道' : '新增渠道'"
      width="820px"
      destroy-on-close
      @closed="resetFormDialog"
    >
      <ChannelForm
        :editing-channel="editingChannel"
        :loading="saving"
        :notice="formNotice"
        notice-type="error"
        @submit="saveChannel"
        @cancel="formDialog.visible = false"
      />
    </el-dialog>

    <el-dialog v-model="testDialog.visible" title="渠道测试结果" width="720px">
      <el-descriptions v-if="testDialog.result" :column="2" border>
        <el-descriptions-item label="结果">
          <el-tag :type="testDialog.result.ok ? 'success' : 'danger'">
            {{ testDialog.result.ok ? '成功' : '失败' }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="状态码">{{ testDialog.result.status_code || '-' }}</el-descriptions-item>
        <el-descriptions-item label="请求模型">{{ testDialog.result.model || '-' }}</el-descriptions-item>
        <el-descriptions-item label="上游模型">{{ testDialog.result.upstream_model || '-' }}</el-descriptions-item>
        <el-descriptions-item label="耗时">{{ formatLatency(testDialog.result.latency_ms) }}</el-descriptions-item>
        <el-descriptions-item label="测试地址">{{ testDialog.result.url || '-' }}</el-descriptions-item>
      </el-descriptions>
      <el-alert
        v-if="testDialog.result && testDialog.result.error"
        class="test-alert"
        :title="testDialog.result.error"
        type="error"
        show-icon
        :closable="false"
      />
      <pre v-if="testDialog.result && testDialog.result.body" class="test-body">{{ testDialog.result.body }}</pre>
    </el-dialog>
  </main>
</template>

<script>
import { createChannel, deleteChannel, fetchChannels, testChannel, updateChannel } from '@/api/channels'
import ChannelForm from './components/ChannelForm.vue'
import ChannelTable from './components/ChannelTable.vue'

export default {
  name: 'ChannelView',
  components: {
    ChannelForm,
    ChannelTable
  },
  data() {
    return {
      channels: [],
      editingChannel: null,
      formDialog: {
        visible: false
      },
      loading: false,
      saving: false,
      testing: false,
      notice: '',
      formNotice: '',
      testDialog: {
        visible: false,
        result: null
      }
    }
  },
  mounted() {
    this.loadChannels()
  },
  methods: {
    async loadChannels() {
      this.loading = true
      try {
        this.channels = await fetchChannels()
      } catch (error) {
        this.notice = error.message
      } finally {
        this.loading = false
      }
    },
    async saveChannel(data) {
      this.saving = true
      this.formNotice = ''
      this.notice = ''
      try {
        if (this.editingChannel) {
          await updateChannel(this.editingChannel.id, data)
          this.notice = '渠道已更新'
        } else {
          await createChannel(data)
          this.notice = '渠道已创建'
        }
        this.formDialog.visible = false
        await this.loadChannels()
      } catch (error) {
        this.formNotice = error.message
      } finally {
        this.saving = false
      }
    },
    openCreateDialog() {
      this.editingChannel = null
      this.formNotice = ''
      this.formDialog.visible = true
    },
    openEditDialog(channel) {
      this.editingChannel = { ...channel }
      this.formNotice = ''
      this.formDialog.visible = true
    },
    resetFormDialog() {
      this.editingChannel = null
      this.formNotice = ''
    },
    async removeChannel(channel) {
      const confirmed = window.confirm(`确定删除渠道 ${channel.name} 吗？`)
      if (!confirmed) {
        return
      }
      try {
        await deleteChannel(channel.id)
        this.notice = '渠道已删除'
        await this.loadChannels()
      } catch (error) {
        this.notice = error.message
      }
    },
    async runChannelTest(channel) {
      if (this.testing) {
        return
      }
      const defaultModel = this.defaultTestModel(channel)
      const model = window.prompt('请输入要测试的模型名', defaultModel)
      if (model === null) {
        return
      }
      this.testing = true
      this.notice = ''
      try {
        const result = await testChannel(channel.id, { model: model.trim() })
        this.testDialog.result = result
        this.testDialog.visible = true
        this.notice = result.ok ? '渠道测试成功' : '渠道测试失败'
      } catch (error) {
        this.testDialog.result = {
          ok: false,
          error: error.message
        }
        this.testDialog.visible = true
        this.notice = error.message
      } finally {
        this.testing = false
      }
    },
    defaultTestModel(channel) {
      const models = this.parseJSON(channel.models, [])
      if (Array.isArray(models) && models.length > 0) {
        return models[0]
      }
      const mapping = this.parseJSON(channel.model_mapping, {})
      const keys = Object.keys(mapping || {})
      return keys[0] || ''
    },
    parseJSON(value, fallback) {
      if (!value) {
        return fallback
      }
      if (typeof value !== 'string') {
        return value
      }
      try {
        return JSON.parse(value)
      } catch (error) {
        return fallback
      }
    },
    formatLatency(value) {
      const ms = Number(value || 0)
      if (ms >= 1000) {
        return `${(ms / 1000).toFixed(2)} s`
      }
      return `${ms} ms`
    }
  }
}
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.page-header p {
  margin-bottom: 0;
}

.page-notice {
  margin-bottom: 16px;
}

.test-alert {
  margin-top: 16px;
}

.test-body {
  max-height: 320px;
  margin: 16px 0 0;
  padding: 14px;
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #0f172a;
  color: #e5e7eb;
  font-size: 12px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}

@media (max-width: 900px) {
  .page-header {
    flex-direction: column;
  }
}
</style>
